import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  DialogContent,
  DialogTitle,
  IconButton,
  Input,
  List,
  ListItem,
  Menu,
  MenuItem,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import MuiPopover from '@mui/material/Popover';
import Collapse from '@mui/material/Collapse';
import Pagination from '@mui/material/Pagination';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoIcon from '@mui/icons-material/Info';
import { formatLogTimestamp } from '../../../helpers/utils';

const LogsModal = ({
  open,
  onClose,
  functionName,
  logs,
  loading,
  logFilters,
  onFilterChange,
  onDeleteSelected,
  onDeleteAll,
  onMarkAllRead,
  onRefresh,
}) => {
  const [selectedLogs, setSelectedLogs] = useState(new Set());
  const [expandedLogs, setExpandedLogs] = useState(new Set());
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [pendingFilters, setPendingFilters] = useState({
    errorFilter: logFilters.errorFilter,
    day: logFilters.day,
  });
  const [searchInput, setSearchInput] = useState(logFilters.search || '');
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(false);
  const logsListRef = useRef(null);

  // Reset selection when logs change
  useEffect(() => {
    setSelectedLogs(new Set());
  }, [logs]);

  // Reset search input when modal opens
  useEffect(() => {
    if (open) {
      setSearchInput(logFilters.search || '');
      setPendingFilters({
        errorFilter: logFilters.errorFilter,
        day: logFilters.day,
      });
    }
  }, [open, logFilters]);

  const handleToggleSelect = (logKey) => {
    setSelectedLogs(prev => {
      const next = new Set(prev);
      if (next.has(logKey)) {
        next.delete(logKey);
      } else {
        next.add(logKey);
      }
      return next;
    });
  };

  const handleToggleExpand = (logKey) => {
    setExpandedLogs(prev => {
      const next = new Set(prev);
      if (next.has(logKey)) {
        next.delete(logKey);
      } else {
        next.add(logKey);
      }
      return next;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedLogs(new Set(logs.map((_, idx) => idx)));
    } else {
      setSelectedLogs(new Set());
    }
  };

  const handleExpandAll = () => {
    if (expandedLogs.size === logs.length) {
      setExpandedLogs(new Set());
    } else {
      setExpandedLogs(new Set(logs.map((_, idx) => idx)));
    }
  };

  const handleSearch = () => {
    onFilterChange({ search: searchInput, page: 1 });
  };

  const handleApplyFilters = () => {
    onFilterChange({
      errorFilter: pendingFilters.errorFilter,
      day: pendingFilters.day,
      page: 1,
    });
    setFilterAnchorEl(null);
  };

  const handleClearFilters = () => {
    setPendingFilters({ errorFilter: 'all', day: '' });
  };

  const handleDeleteSelected = async () => {
    const indices = Array.from(selectedLogs);
    await onDeleteSelected(indices);
    setSelectedLogs(new Set());
  };

  const handleDeleteAll = async () => {
    setConfirmDeleteAll(false);
    setMenuAnchorEl(null);
    await onDeleteAll();
  };

  const hasActiveFilters = logFilters.errorFilter !== 'all' || logFilters.day;

  const isValidLog = (log) => {
    return (
      log &&
      typeof log === 'object' &&
      typeof log.log === 'string' &&
      typeof log.level === 'string' &&
      typeof log.timestamp === 'string'
    );
  };

  const allLogsValid = logs.every(isValidLog);

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <ModalDialog sx={{ maxWidth: 900, width: '85vw', maxHeight: '90vh' }}>
          <ModalClose />
          <DialogTitle>
            <Stack sx={{ width: '100%' }}>
              <Typography sx={{ mb: 1, fontWeight: 600, fontSize: '1.1rem' }}>
                Function Logs
                {functionName && (
                  <Typography component="span" sx={{ ml: 1, fontWeight: 400, fontSize: '1rem', color: 'neutral.600' }}>
                    ({functionName})
                  </Typography>
                )}
              </Typography>
              
              {/* Toolbar */}
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <Checkbox
                  size="sm"
                  checked={logs.length > 0 && selectedLogs.size === logs.length}
                  indeterminate={selectedLogs.size > 0 && selectedLogs.size < logs.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  disabled={logs.length === 0}
                />
                <Input
                  size="sm"
                  placeholder="Search logs..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  sx={{ width: 200 }}
                  endDecorator={
                    searchInput && (
                      <IconButton
                        size="sm"
                        variant="plain"
                        onClick={() => {
                          setSearchInput('');
                          onFilterChange({ search: '', page: 1 });
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )
                  }
                />
                <Button
                  size="sm"
                  variant="solid"
                  sx={{ minWidth: 0, px: 1.5 }}
                  onClick={handleSearch}
                >
                  <SearchIcon fontSize="small" />
                </Button>

                {/* Filter button */}
                <Box sx={{ position: 'relative' }}>
                  <IconButton
                    size="sm"
                    variant="plain"
                    onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                    sx={{ color: hasActiveFilters ? 'primary.500' : undefined }}
                  >
                    <FilterListIcon fontSize="small" />
                  </IconButton>
                  {hasActiveFilters && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        width: 8,
                        height: 8,
                        bgcolor: 'primary.500',
                        borderRadius: '50%',
                        border: '2px solid white',
                      }}
                    />
                  )}
                </Box>

                {/* Delete selected */}
                <IconButton
                  size="sm"
                  variant="plain"
                  color="danger"
                  disabled={selectedLogs.size === 0}
                  onClick={handleDeleteSelected}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>

                <Box sx={{ flex: 1 }} />

                {/* More menu */}
                <IconButton
                  size="sm"
                  variant="plain"
                  onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </DialogTitle>

          <DialogContent>
            {logs.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
                <Typography level="body-md" color="neutral" sx={{ textAlign: 'center' }}>
                  No logs available
                </Typography>
              </Box>
            ) : !allLogsValid ? (
              <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: 'background.level1', my: 2 }}>
                <InfoIcon sx={{ fontSize: 32, mb: 1, color: 'warning.500' }} />
                <Typography level="title-md" sx={{ mb: 1 }}>
                  Malformed log data
                </Typography>
                <Typography level="body-sm" color="neutral">
                  Some log entries have an invalid format and cannot be displayed.
                </Typography>
              </Box>
            ) : (
              <List
                ref={logsListRef}
                sx={{
                  height: 500,
                  overflowY: 'auto',
                  pr: 1,
                  scrollbarWidth: 'thin',
                }}
              >
                {logs.map((log, idx) => {
                  const hasOutput = !!(log.output || log.console_output);
                  const isExpanded = expandedLogs.has(idx);
                  const isSelected = selectedLogs.has(idx);

                  return (
                    <ListItem key={idx} sx={{ px: 0 }}>
                      <Stack
                        spacing={0.75}
                        sx={{
                          p: 1.5,
                          width: '100%',
                          borderRadius: 'md',
                          bgcolor: isSelected
                            ? 'primary.softBg'
                            : log.unread
                            ? 'primary.100'
                            : isExpanded
                            ? 'background.level2'
                            : 'background.level1',
                          border: '1px solid',
                          borderColor: isSelected
                            ? 'primary.300'
                            : log.unread
                            ? 'primary.200'
                            : 'divider',
                          transition: 'all 0.15s',
                        }}
                      >
                        {/* Header row */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Checkbox
                            size="sm"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(idx)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                            [{formatLogTimestamp(log.timestamp)}]
                          </Typography>
                          {log.task_id && (
                            <Typography level="body-xs" color="neutral">
                              Task: {log.task_id}
                            </Typography>
                          )}
                          <Box sx={{ flex: 1 }} />
                          {hasOutput && (
                            <IconButton
                              size="sm"
                              variant="plain"
                              onClick={() => handleToggleExpand(idx)}
                              sx={{
                                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s',
                              }}
                            >
                              <ExpandMoreIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Stack>

                        {/* Log content */}
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <Chip
                            size="sm"
                            color={
                              log.level === 'error'
                                ? 'danger'
                                : log.level === 'warning'
                                ? 'warning'
                                : 'success'
                            }
                          >
                            {log.level.toUpperCase()}
                          </Chip>
                          <Typography level="body-sm" sx={{ wordBreak: 'break-word', flex: 1 }}>
                            {log.log}
                          </Typography>
                        </Stack>

                        {/* Expandable output */}
                        {hasOutput && (
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Stack spacing={1} sx={{ mt: 1, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {log.output && (
                                <Box>
                                  <Typography level="body-xs" fontWeight="bold" sx={{ mb: 0.5 }}>
                                    Output:
                                  </Typography>
                                  <Box
                                    sx={{
                                      whiteSpace: 'pre-wrap',
                                      bgcolor: 'background.level1',
                                      p: 1,
                                      borderRadius: 'sm',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    {typeof log.output === 'string' || typeof log.output === 'number'
                                      ? log.output
                                      : JSON.stringify(log.output, null, 2)}
                                  </Box>
                                </Box>
                              )}
                              {log.console_output && (
                                <Box>
                                  <Typography level="body-xs" fontWeight="bold" sx={{ mb: 0.5 }}>
                                    Console Output:
                                  </Typography>
                                  <Box
                                    sx={{
                                      whiteSpace: 'pre-wrap',
                                      bgcolor: 'background.level1',
                                      p: 1,
                                      borderRadius: 'sm',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    {typeof log.console_output === 'string' || typeof log.console_output === 'number'
                                      ? log.console_output
                                      : JSON.stringify(log.console_output, null, 2)}
                                  </Box>
                                </Box>
                              )}
                            </Stack>
                          </Collapse>
                        )}
                      </Stack>
                    </ListItem>
                  );
                })}
              </List>
            )}

            {/* Pagination */}
            {logs.length > 0 && (
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
                <Box />
                <Pagination
                  count={Math.ceil(logFilters.total / logFilters.pageSize)}
                  page={logFilters.page}
                  onChange={(_, page) => onFilterChange({ page })}
                />
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography level="body-sm">Rows:</Typography>
                  <Select
                    size="sm"
                    value={logFilters.pageSize}
                    onChange={(_, v) => onFilterChange({ pageSize: Number(v), page: 1 })}
                    sx={{ minWidth: 80 }}
                  >
                    <Option value={10}>10</Option>
                    <Option value={20}>20</Option>
                    <Option value={50}>50</Option>
                    <Option value={100}>100</Option>
                  </Select>
                </Stack>
              </Stack>
            )}
          </DialogContent>

          {/* Filter Popover */}
          <MuiPopover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={() => setFilterAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: { sx: { p: 2, minWidth: 200 } } }}
          >
            <Stack spacing={2}>
              <Typography level="body-sm" fontWeight={600}>Filter logs</Typography>
              <Select
                size="sm"
                value={pendingFilters.errorFilter}
                onChange={(_, v) => setPendingFilters(prev => ({ ...prev, errorFilter: v || 'all' }))}
              >
                <Option value="all">All Levels</Option>
                <Option value="error">Error</Option>
                <Option value="info">Info</Option>
              </Select>
              <Input
                size="sm"
                type="date"
                value={pendingFilters.day}
                onChange={(e) => setPendingFilters(prev => ({ ...prev, day: e.target.value }))}
                placeholder="Date"
              />
              <Button size="sm" onClick={handleApplyFilters}>Apply</Button>
              <Button size="sm" variant="plain" onClick={handleClearFilters}>Clear</Button>
            </Stack>
          </MuiPopover>

          {/* More Menu */}
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={() => setMenuAnchorEl(null)}
            placement="bottom-end"
          >
            <MenuItem onClick={handleExpandAll}>
              {expandedLogs.size === logs.length ? 'Collapse all outputs' : 'Expand all outputs'}
            </MenuItem>
            <MenuItem onClick={() => { onRefresh(); setMenuAnchorEl(null); }}>
              Refresh logs
            </MenuItem>
            <MenuItem onClick={() => { onMarkAllRead(); setMenuAnchorEl(null); }}>
              Mark all as read
            </MenuItem>
            <MenuItem
              onClick={() => { setMenuAnchorEl(null); setConfirmDeleteAll(true); }}
              sx={{ color: 'danger.plainColor' }}
            >
              Delete all logs
            </MenuItem>
          </Menu>
        </ModalDialog>
      </Modal>

      {/* Confirm Delete All Modal */}
      <Modal open={confirmDeleteAll} onClose={() => setConfirmDeleteAll(false)}>
        <ModalDialog variant="outlined">
          <ModalClose />
          <DialogTitle>Delete All Logs</DialogTitle>
          <DialogContent>
            Are you sure you want to delete all logs? This cannot be undone.
          </DialogContent>
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="plain" color="neutral" onClick={() => setConfirmDeleteAll(false)}>
              Cancel
            </Button>
            <Button color="danger" onClick={handleDeleteAll}>
              Delete All
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default LogsModal;
